import React, { useState, useEffect, useMemo } from "react";
import { X, History, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Star, ArrowRight } from "lucide-react";
import { db } from "../../firebase/config";
import { collection, onSnapshot } from "firebase/firestore";

const PAGE_SIZE = 100;

export default function WeeklyStatusLogsModal({ isOpen, onClose }) {
  const [items, setItems] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Subscribe to weekly status items and headers
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);

    const unsubItems = onSnapshot(collection(db, "weekly_status_items"), (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      // Sort newest first by timestamp or weekDate
      list.sort((a, b) => {
        const timeA = new Date(a.timestamp || a.weekDate || 0).getTime();
        const timeB = new Date(b.timestamp || b.weekDate || 0).getTime();
        return timeB - timeA;
      });
      setItems(list);
      setLoading(false);
    }, (err) => {
      console.error("Error loading weekly status items:", err);
      setLoading(false);
    });

    const unsubHeaders = onSnapshot(collection(db, "weekly_status_headers"), (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      setHeaders(list);
    }, (err) => {
      console.error("Error loading weekly status headers:", err);
    });

    return () => {
      unsubItems();
      unsubHeaders();
    };
  }, [isOpen]);

  // Lookup maps for headers
  const headersMap = useMemo(() => {
    const map = {};
    headers.forEach(h => {
      map[h.id] = h.name;
    });
    return map;
  }, [headers]);

  // Date Formatter helpers
  const formatWeekRange = (mondayStr) => {
    if (!mondayStr) return "N/A";
    try {
      const monday = new Date(mondayStr + "T00:00:00");
      const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000);
      const options = { month: "short", day: "numeric", year: "numeric" };
      return `${monday.toLocaleDateString(undefined, options)} – ${sunday.toLocaleDateString(undefined, options)}`;
    } catch {
      return mondayStr;
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return ts;
    }
  };

  // Filter items: ONLY 'Atlas Control Change Log' section, sorted newest to oldest
  const filteredItems = useMemo(() => {
    return items.filter(it => {
      const sectionName = (headersMap[it.headerId] || it.headerId || "").trim().toLowerCase();
      return sectionName === "atlas control change log";
    });
  }, [items, headersMap]);

  // Pagination calculation
  const totalItems = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalItems);
  const currentRows = filteredItems.slice(startIndex, endIndex);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div 
        className="modal-content glass-card animate-slide-up" 
        style={{ 
          maxWidth: "1150px", 
          width: "95%", 
          maxHeight: "92vh", 
          display: "flex", 
          flexDirection: "column",
          padding: 0,
          overflow: "hidden"
        }}
      >
        {/* Modal Header */}
        <div className="modal-header" style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid var(--border-color)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <div style={{ 
              width: "36px", 
              height: "36px", 
              borderRadius: "8px", 
              backgroundColor: "rgba(52, 152, 219, 0.15)", 
              color: "var(--primary)", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center" 
            }}>
              <History size={20} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "var(--text-primary)" }}>
                  Atlas Control Change Logs
                </h3>
                <span className="badge" style={{ backgroundColor: "var(--primary-light)", color: "var(--primary)", fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}>
                  {totalItems} entries
                </span>
                <span className="badge" style={{ backgroundColor: "rgba(255,255,255,0.06)", color: "var(--text-secondary)", fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}>
                  Read Only
                </span>
              </div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose} type="button" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {/* Table Container */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: "350px", backgroundColor: "var(--bg-main)" }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-secondary)" }}>
              <div className="spinner" style={{ margin: "0 auto 1rem auto" }} />
              Loading change logs...
            </div>
          ) : currentRows.length === 0 ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
              <History size={40} style={{ opacity: 0.3, marginBottom: "0.75rem" }} />
              <p style={{ margin: 0, fontWeight: 600 }}>No Atlas Control change log entries found.</p>
            </div>
          ) : (
            <table className="data-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "var(--bg-card)", zIndex: 1, borderBottom: "2px solid var(--border-color)" }}>
                <tr>
                  <th style={{ padding: "0.75rem 1rem", width: "180px", textAlign: "left" }}>Week Range</th>
                  <th style={{ padding: "0.75rem 1rem", minWidth: "260px", textAlign: "left" }}>Change Log Item</th>
                  <th style={{ padding: "0.75rem 1rem", minWidth: "320px", textAlign: "left" }}>Notes & Details</th>
                  <th style={{ padding: "0.75rem 1rem", width: "110px", textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row, idx) => {
                  const isStarred = Boolean(row.isStarred);
                  const isRolledOver = Boolean(row.sourceItemId);

                  return (
                    <tr 
                      key={row.id || idx}
                      style={{ 
                        borderBottom: "1px solid var(--border-color)",
                        transition: "background-color 0.15s ease"
                      }}
                      className="table-row-hover"
                    >
                      {/* Week Date Range */}
                      <td style={{ padding: "0.75rem 1rem", verticalAlign: "top" }}>
                        <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                          {formatWeekRange(row.weekDate)}
                        </div>
                        {row.timestamp && (
                          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "2px" }}>
                            {formatTimestamp(row.timestamp)}
                          </div>
                        )}
                      </td>

                      {/* Item */}
                      <td style={{ padding: "0.75rem 1rem", verticalAlign: "top" }}>
                        <div style={{ fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.4 }}>
                          {row.item || "(Untitled Item)"}
                        </div>
                      </td>

                      {/* Notes */}
                      <td style={{ padding: "0.75rem 1rem", verticalAlign: "top" }}>
                        <div style={{ 
                          color: "var(--text-secondary)", 
                          lineHeight: 1.45, 
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word"
                        }}>
                          {row.notes || <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No notes provided</span>}
                        </div>
                      </td>

                      {/* Status / Attributes */}
                      <td style={{ padding: "0.75rem 1rem", verticalAlign: "top", textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "center" }}>
                          {isStarred && (
                            <span 
                              className="badge" 
                              style={{ 
                                backgroundColor: "rgba(245, 158, 11, 0.15)", 
                                color: "#f59e0b", 
                                border: "1px solid rgba(245, 158, 11, 0.3)",
                                fontSize: "0.7rem",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.2rem",
                                padding: "0.15rem 0.4rem"
                              }}
                            >
                              <Star size={10} fill="#f59e0b" /> Starred
                            </span>
                          )}
                          {isRolledOver && (
                            <span 
                              className="badge" 
                              style={{ 
                                backgroundColor: "rgba(59, 130, 246, 0.15)", 
                                color: "#60a5fa", 
                                border: "1px solid rgba(59, 130, 246, 0.3)",
                                fontSize: "0.7rem",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.2rem",
                                padding: "0.15rem 0.4rem"
                              }}
                            >
                              <ArrowRight size={10} /> Rolled Over
                            </span>
                          )}
                          {!isStarred && !isRolledOver && (
                            <span style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal Footer & Pagination */}
        <div style={{ 
          padding: "0.85rem 1.5rem", 
          backgroundColor: "var(--bg-card)", 
          borderTop: "1px solid var(--border-color)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem"
        }}>
          {/* Record Count Range */}
          <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
            {totalItems > 0 ? (
              <>
                Showing <strong style={{ color: "var(--text-primary)" }}>{startIndex + 1}</strong> to <strong style={{ color: "var(--text-primary)" }}>{endIndex}</strong> of <strong style={{ color: "var(--text-primary)" }}>{totalItems}</strong> entries
              </>
            ) : (
              "0 entries"
            )}
          </div>

          {/* Pagination Controls */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage <= 1}
              style={{ padding: "0.3rem 0.5rem", minHeight: "auto" }}
              title="First Page"
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
              style={{ padding: "0.3rem 0.5rem", minHeight: "auto" }}
              title="Previous Page"
            >
              <ChevronLeft size={16} />
            </button>

            <span style={{ fontSize: "0.85rem", color: "var(--text-primary)", padding: "0 0.5rem" }}>
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              style={{ padding: "0.3rem 0.5rem", minHeight: "auto" }}
              title="Next Page"
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages}
              style={{ padding: "0.3rem 0.5rem", minHeight: "auto" }}
              title="Last Page"
            >
              <ChevronsRight size={16} />
            </button>
          </div>

          {/* Close Action */}
          <button type="button" className="btn btn-secondary" onClick={onClose} style={{ fontSize: "0.85rem" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
